import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Network, Search, RefreshCw, Download, ChevronDown, ChevronRight,
  Server, MapPin, Building2, Wifi, AlertTriangle, CircleCheck, TriangleAlert, Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Distribusi IP Pelanggan & Router
 * Menggantikan halaman Subnet Planner lama.
 *
 * Menampilkan hierarki: Tenant -> Cabang -> Router -> Subnet
 * dengan progress bar berwarna (hijau/kuning/merah) dan detail
 * IP terpakai / reserved / L2TP tunnel.
 */
const SubnetPlanner = () => {
  const { selectedBranch, selectedRouter } = useApp();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: null, tenants: [] });
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set()); // holds keys like "t:xxx", "b:xxx", "r:xxx"

  // --- Add Subnet Modal state ---
  const [showAddModal, setShowAddModal] = useState(false);
  const [addRouterId, setAddRouterId] = useState('');
  const [addCidr, setAddCidr] = useState('');
  const [addReserved, setAddReserved] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Flatten all routers from data for the select dropdown
  const allRouters = useMemo(() => {
    const routers = [];
    data.tenants.forEach((t) => {
      t.branches?.forEach((b) => {
        b.routers?.forEach((r) => routers.push(r));
      });
    });
    return routers;
  }, [data.tenants]);

  const handleAddSubnet = async () => {
    if (!addRouterId) {
      toast.error('Pilih router terlebih dahulu');
      return;
    }
    if (!addCidr.trim()) {
      toast.error('CIDR subnet wajib diisi');
      return;
    }
    setAddSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = API_URL + '/api/v2/ip-distribution/add-subnet';
      const res = await axios.post(
        url,
        {
          router_id: parseInt(addRouterId),
          cidr: addCidr.trim(),
          reserved_ips: addReserved.trim() || null,
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      if (res.data && res.data.success) {
        toast.success(res.data.message || 'Subnet berhasil ditambahkan');
        setShowAddModal(false);
        setAddRouterId('');
        setAddCidr('');
        setAddReserved('');
        fetchData();
      }
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.detail) || e.message || 'Gagal menambah subnet';
      toast.error(msg);
    } finally {
      setAddSubmitting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedBranch && selectedBranch !== 'all') params.branch_id = selectedBranch;
      if (selectedRouter && selectedRouter !== 'all') params.router_id = selectedRouter;
      const res = await axios.get(`${API_URL}/api/v2/ip-distribution/overview`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      if (res.data?.success) {
        setData({ summary: res.data.summary, tenants: res.data.tenants || [] });
        // Auto-expand top level
        const initialExpanded = new Set();
        (res.data.tenants || []).forEach((t) => {
          initialExpanded.add(`t:${t.tenant_id || '_none'}`);
        });
        setExpanded(initialExpanded);
      }
    } catch (e) {
      toast.error('Gagal memuat distribusi IP: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [selectedBranch, selectedRouter]);

  const toggle = (key) => {
    const s = new Set(expanded);
    if (s.has(key)) s.delete(key); else s.add(key);
    setExpanded(s);
  };

  const expandAll = () => {
    const s = new Set();
    data.tenants.forEach((t) => {
      s.add(`t:${t.tenant_id || '_none'}`);
      t.branches?.forEach((b) => {
        s.add(`b:${t.tenant_id || '_none'}:${b.branch_id || '_none'}`);
        b.routers?.forEach((r) => s.add(`r:${r.router_id}`));
      });
    });
    setExpanded(s);
  };
  const collapseAll = () => setExpanded(new Set());

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      if (selectedRouter && selectedRouter !== 'all') params.append('router_id', selectedRouter);
      const url = `${API_URL}/api/v2/ip-distribution/export.csv?${params}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ip-distribution-${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      toast.success('CSV berhasil diunduh');
    } catch (e) {
      toast.error('Gagal export CSV');
    }
  };

  // Filter by search string (matches router name, cidr, or branch)
  const filteredTenants = useMemo(() => {
    if (!search.trim()) return data.tenants;
    const q = search.toLowerCase();
    return data.tenants
      .map((t) => {
        const branches = (t.branches || [])
          .map((b) => {
            const routers = (b.routers || []).filter((r) => {
              const hit =
                (r.router_name || '').toLowerCase().includes(q) ||
                (r.host || '').toLowerCase().includes(q) ||
                (b.branch_name || '').toLowerCase().includes(q) ||
                (t.tenant_name || '').toLowerCase().includes(q) ||
                (r.subnets || []).some((s) => (s.cidr || '').toLowerCase().includes(q));
              return hit;
            });
            return { ...b, routers };
          })
          .filter((b) => b.routers.length > 0);
        return { ...t, branches };
      })
      .filter((t) => t.branches.length > 0);
  }, [data.tenants, search]);

  const statusColor = (status) => {
    if (status === 'critical') return 'bg-rose-500';
    if (status === 'warning') return 'bg-amber-500';
    return 'bg-emerald-500';
  };
  const statusLabel = (status) => {
    if (status === 'critical') return 'Penuh';
    if (status === 'warning') return 'Ramai';
    return 'Aman';
  };
  const statusIcon = (status) => {
    if (status === 'critical') return <AlertTriangle className="w-3.5 h-3.5" />;
    if (status === 'warning') return <TriangleAlert className="w-3.5 h-3.5" />;
    return <CircleCheck className="w-3.5 h-3.5" />;
  };

  const s = data.summary;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6" data-testid="ip-distribution-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Network className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-slate-800 dark:text-slate-100" data-testid="ip-distribution-title">
              Distribusi IP Pelanggan
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Ringkasan pemakaian IP per tenant, cabang, router, dan subnet
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="default"
            onClick={() => setShowAddModal(true)}
            data-testid="btn-add-subnet"
            size="sm"
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Tambah Subnet
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="btn-export-csv" size="sm">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" onClick={fetchData} data-testid="btn-refresh" size="sm">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total Router" value={s.total_routers} icon={Server} tint="from-indigo-500 to-blue-500" testid="stat-routers" />
          <StatCard label="Total Subnet" value={s.total_subnets} icon={Network} tint="from-emerald-500 to-teal-500" testid="stat-subnets" />
          <StatCard label="IP Terpakai" value={`${s.total_ips_used} / ${s.total_ips_capacity}`} icon={Wifi} tint="from-amber-500 to-orange-500" testid="stat-ips" sub={`${s.usage_pct || 0}%`} />
          <StatCard label="Tunnel L2TP" value={s.total_vpn_tunnels} icon={MapPin} tint="from-rose-500 to-pink-500" testid="stat-vpn" />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Cari nama router, host, cabang, atau CIDR..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll} data-testid="btn-expand-all">Buka Semua</Button>
          <Button variant="ghost" size="sm" onClick={collapseAll} data-testid="btn-collapse-all">Tutup Semua</Button>
        </div>
      </div>

      {/* Add Subnet Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-subnet-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-emerald-600" />
              Tambah Subnet Baru
            </DialogTitle>
            <DialogDescription>
              Tambahkan subnet baru ke Subnet Bank untuk auto-allocation Framed-IP RADIUS.
              Subnet akan langsung aktif setelah disimpan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Router Select */}
            <div className="space-y-2">
              <Label htmlFor="add-router">Router <span className="text-rose-500">*</span></Label>
              <Select value={addRouterId} onValueChange={setAddRouterId}>
                <SelectTrigger data-testid="select-router">
                  <SelectValue placeholder="Pilih router MikroTik..." />
                </SelectTrigger>
                <SelectContent>
                  {allRouters.map((r) => (
                    <SelectItem key={r.router_id} value={String(r.router_id)}>
                      {r.router_name} ({r.host}:{r.port})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* CIDR Input */}
            <div className="space-y-2">
              <Label htmlFor="add-cidr">CIDR Subnet <span className="text-rose-500">*</span></Label>
              <Input
                id="add-cidr"
                placeholder="contoh: 10.30.4.0/24"
                value={addCidr}
                onChange={(e) => setAddCidr(e.target.value)}
                data-testid="input-cidr"
              />
              <p className="text-xs text-slate-500">
                Format: IP/prefixlen (max /24). Contoh: <code>10.30.4.0/24</code> atau <code>172.16.20.0/24</code>
              </p>
            </div>

            {/* Reserved IPs */}
            <div className="space-y-2">
              <Label htmlFor="add-reserved">IP Reserved (opsional)</Label>
              <Input
                id="add-reserved"
                placeholder="contoh: 10.30.4.0,10.30.4.1,10.30.4.255"
                value={addReserved}
                onChange={(e) => setAddReserved(e.target.value)}
                data-testid="input-reserved"
              />
              <p className="text-xs text-slate-500">
                Pisahkan dengan koma. Default jika kosong: network, gateway (.1), broadcast (.255)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              data-testid="btn-cancel-add"
            >
              Batal
            </Button>
            <Button
              onClick={handleAddSubnet}
              disabled={addSubmitting || !addRouterId || !addCidr.trim()}
              data-testid="btn-submit-add"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {addSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Menyimpan...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1.5" /> Tambah Subnet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-slate-400 dark:text-slate-500" data-testid="loading-indicator">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
          Memuat data distribusi...
        </div>
      ) : filteredTenants.length === 0 ? (
        <div className="text-center py-16 text-slate-400 dark:text-slate-500" data-testid="empty-state">
          Tidak ada data distribusi IP yang cocok.
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTenants.map((t) => {
            const tKey = `t:${t.tenant_id || '_none'}`;
            const tOpen = expanded.has(tKey);
            return (
              <Card key={tKey} className="overflow-hidden border-slate-200 dark:border-slate-700">
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-700/40"
                  onClick={() => toggle(tKey)}
                  data-testid={`row-tenant-${t.tenant_id || 'none'}`}
                >
                  {tOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <Building2 className="w-5 h-5 text-indigo-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{t.tenant_name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t.total_routers} router · {t.branches?.length || 0} cabang · {t.total_used} / {t.total_capacity} IP
                    </div>
                  </div>
                  <UsageBar pct={t.usage_pct} status={t.status} width="w-32 sm:w-48" />
                  <Badge className={`text-white ${statusColor(t.status)}`}>{statusLabel(t.status)}</Badge>
                </div>
                {tOpen && (
                  <div className="border-t border-slate-100 dark:border-slate-700 px-2 sm:px-4 py-2 space-y-2 bg-slate-50/50 dark:bg-slate-900/20">
                    {t.branches.map((b) => {
                      const bKey = `b:${t.tenant_id || '_none'}:${b.branch_id || '_none'}`;
                      const bOpen = expanded.has(bKey);
                      return (
                        <div key={bKey} className="rounded-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                          <div
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30"
                            onClick={() => toggle(bKey)}
                            data-testid={`row-branch-${b.branch_id || 'none'}`}
                          >
                            {bOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            <MapPin className="w-4 h-4 text-teal-500" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-700 dark:text-slate-200 truncate">{b.branch_name}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {b.routers.length} router · {b.total_used} / {b.total_capacity} IP
                              </div>
                            </div>
                            <UsageBar pct={b.usage_pct} status={b.status} width="w-24 sm:w-40" />
                          </div>
                          {bOpen && (
                            <div className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                              {b.routers.map((r) => (
                                <RouterRow
                                  key={`r:${r.router_id}`}
                                  router={r}
                                  open={expanded.has(`r:${r.router_id}`)}
                                  onToggle={() => toggle(`r:${r.router_id}`)}
                                  statusColor={statusColor}
                                  statusIcon={statusIcon}
                                  statusLabel={statusLabel}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ------- Small components ------- */

const StatCard = ({ label, value, icon: Icon, tint, testid, sub }) => (
  <Card className="border-slate-200 dark:border-slate-700" data-testid={testid}>
    <CardContent className="p-3 sm:p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tint} flex items-center justify-center shadow`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{label}</div>
        <div className="text-lg font-bold text-slate-800 dark:text-slate-100 truncate">{value}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

const UsageBar = ({ pct = 0, status, width = 'w-full' }) => {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const bg =
    status === 'critical'
      ? 'bg-rose-500'
      : status === 'warning'
      ? 'bg-amber-500'
      : 'bg-emerald-500';
  return (
    <div className={`${width} shrink-0`} title={`${clamped}%`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div className={`h-full ${bg} transition-all`} style={{ width: `${clamped}%` }} />
        </div>
        <div className="text-xs font-medium text-slate-600 dark:text-slate-300 tabular-nums w-12 text-right">
          {clamped}%
        </div>
      </div>
    </div>
  );
};

const RouterRow = ({ router: r, open, onToggle, statusColor, statusIcon, statusLabel }) => {
  return (
    <div className="bg-slate-50/40 dark:bg-slate-900/10" data-testid={`row-router-${r.router_id}`}>
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-100/60 dark:hover:bg-slate-800/40"
        onClick={onToggle}
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <Server className="w-4 h-4 text-indigo-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{r.router_name}</span>
            {r.host && <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{r.host}:{r.port}</span>}
            {!r.is_active && <Badge variant="outline" className="text-xs">Nonaktif</Badge>}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {r.subnets?.length || 0} subnet · {r.total_used} / {r.total_capacity} IP · {r.vpn_tunnels?.length || 0} L2TP
          </div>
        </div>
        <UsageBar pct={r.usage_pct} status={r.status} width="w-20 sm:w-32" />
        <Badge className={`text-white gap-1 ${statusColor(r.status)}`}>
          {statusIcon(r.status)}
          <span className="hidden sm:inline">{statusLabel(r.status)}</span>
        </Badge>
      </div>
      {open && (
        <div className="pl-8 sm:pl-12 pr-3 py-2 space-y-2">
          {r.vpn_tunnels?.length > 0 && (
            <div className="text-xs text-slate-600 dark:text-slate-300 flex flex-wrap gap-1.5 items-center">
              <span className="font-medium">L2TP Tunnel:</span>
              {r.vpn_tunnels.map((v, i) => (
                <Badge key={i} variant="outline" className="font-mono text-[10px]">
                  {v.ip_address}
                </Badge>
              ))}
            </div>
          )}
          {r.subnets?.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-2">Belum ada subnet ter-alokasi ke router ini.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs" data-testid={`subnet-table-${r.router_id}`}>
                <thead>
                  <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-1.5 pr-3">CIDR</th>
                    <th className="py-1.5 pr-3">Total</th>
                    <th className="py-1.5 pr-3">Reserved</th>
                    <th className="py-1.5 pr-3">Terpakai</th>
                    <th className="py-1.5 pr-3">Bebas</th>
                    <th className="py-1.5 pr-3">Progress</th>
                    <th className="py-1.5 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {r.subnets.map((s) => (
                    <tr key={s.subnet_bank_id} className="border-b border-slate-100 dark:border-slate-800/70">
                      <td className="py-1.5 pr-3 font-mono text-slate-700 dark:text-slate-200">{s.cidr}</td>
                      <td className="py-1.5 pr-3 tabular-nums">{s.total_ips}</td>
                      <td className="py-1.5 pr-3 tabular-nums text-slate-400" title={s.reserved_list?.join(', ')}>{s.reserved_ips}</td>
                      <td className="py-1.5 pr-3 tabular-nums font-medium">
                        {s.used_active}
                        {s.used_revoked > 0 && <span className="text-slate-400 text-[10px] ml-1">(+{s.used_revoked} revoked)</span>}
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums">{s.free_ips}</td>
                      <td className="py-1.5 pr-3 w-32"><UsageBar pct={s.usage_pct} status={s.status} width="w-full" /></td>
                      <td className="py-1.5 pr-3">
                        <Badge className={`text-white text-[10px] ${statusColor(s.status)}`}>{statusLabel(s.status)}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubnetPlanner;
